import { pruneGoalFromEwsState } from '../../utils';

export type ReviewGoalsResult = {
    // Realized goals the advisor is removing from the plan.
    realizedGoalIds: string[];
    // Not-realized goals and their new target date (stored as DD-MM-YYYY).
    notRealizedUpdates: { goalId: string; newTargetDate: string }[];
    // Notification updates for goals.
    notificationUpdates?: { notification_id?: string; status?: string }[];
    // Event updates for goals.
    eventUpdates?: { event_type: string; goal_id: string }[];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const applyNewDate = (data: any, newDate: string) => ({
    ...data,
    targetDate: newDate,
    ...(data?.goalDate !== undefined ? { goalDate: newDate } : {})
});

// AEX-3218: apply the advisor's Review-Goals decisions to `adviceGoals` IN THE STORE only (no BE).
// Realized goals are removed exactly like the Goals-step delete (deleteInlineGoal); not-realized goals
// get their target date updated (the category is preserved by the picker window). Persistence to the BE
// happens later at Save Plan. Returns a new object; the input is not mutated.
export const buildUpdatedGoalsAfterReview = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    adviceGoals: any,
    result: ReviewGoalsResult,
    ff3021: boolean
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any => {
    const next = JSON.parse(JSON.stringify(adviceGoals ?? {}));
    const realizedSet = new Set(result.realizedGoalIds);
    const dateById = new Map(result.notRealizedUpdates.map((update) => [update.goalId, update.newTargetDate]));

    // 1. Delete realized goals from every goal-keyed slice (mirrors deleteInlineGoal).
    if (realizedSet.size) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        next.formFields = (next.formFields ?? []).filter((goal: any) => !realizedSet.has(goal.goalId));
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        next.request = (next.request ?? []).filter((item: any) => !realizedSet.has(item?.goalId));
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        next.response = (next.response ?? []).filter((item: any) => !realizedSet.has(item?.goalId));
        const global = next.global ?? {};
        global.responseGoalCalculator = (global.responseGoalCalculator ?? []).filter(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (item: any) => !realizedSet.has(item?.goalId)
        );
        let ewsRequests = global.ewsRequests;
        let ewsResponses = global.ewsResponses;
        result.realizedGoalIds.forEach((goalId) => {
            ({ ewsRequests, ewsResponses } = pruneGoalFromEwsState(ewsRequests, ewsResponses, goalId));
        });
        global.ewsRequests = ewsRequests;
        global.ewsResponses = ewsResponses;
        next.global = global;
    }

    // 2. Re-date not-realized goals (category stays the same, enforced by the picker window).
    if (dateById.size) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        next.formFields = (next.formFields ?? []).map((goal: any) =>
            dateById.has(goal.goalId)
                ? {
                      ...goal,
                      data: {
                          ...applyNewDate(goal.data, dateById.get(goal.goalId)!),
                          ...(ff3021 ? { update: true } : {})
                      }
                  }
                : goal
        );
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        next.request = (next.request ?? []).map((item: any) =>
            item?.goalId && dateById.has(item.goalId) && item?.data
                ? { ...item, data: applyNewDate(item.data, dateById.get(item.goalId)!) }
                : item
        );
    }

    // 3. Under FF AEX-3021, flag the remaining unchanged goals as not-updated (mirrors the goals step).
    if (ff3021 && (realizedSet.size || dateById.size)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        next.formFields = (next.formFields ?? []).map((goal: any) =>
            goal.goalId && !dateById.has(goal.goalId) ? { ...goal, data: { ...goal.data, update: false } } : goal
        );
    }

    return next;
};

// AEX-3218: the goals with the review decisions applied, plus the notification acks and goal events that
// Save Plan sends to the BE — what Reallocate commits to the store, reused when the advisor leaves step 2
// through Profile & Finances instead. Returns a new object; the input is not mutated.
export const applyReviewDecisions = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    adviceGoals: any,
    result: ReviewGoalsResult,
    ff3021: boolean
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any => ({
    ...buildUpdatedGoalsAfterReview(adviceGoals, result, ff3021),
    events: result.eventUpdates ?? [],
    notifications: result.notificationUpdates ?? []
});
